import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { useFocusHeadingOnNavigate } from '../../ui/focus';
import { Pill, ScoreValue, Suppressed } from '../../ui/DataBits';
import { MoodSplit } from '../../ui/MoodSplit';
import { bandOf } from '../../ui/score';
import { loc, type Loc } from '../../lib/loc';
import { TeacherFrame } from './TeacherFrame';
import { useTeacherHome } from './hooks';
import { errorText, rpc, type AppError, type Breakdown } from './api';

// 0026's frontend (spec §12.G, §14.6): the teacher's own monthly STARL
// loop, one class at a time. Four steps -- Before (a prediction, asked
// before any data is shown, same spirit as the per-session
// teacher_set_prediction), Sense (the rolling 4-week trend,
// teacher_class_trend), Think (a selectable interpretation + optional
// note) and Act (one playbook idea, no topic filter -- the spec's own
// words are "drawn from the existing playbook", and school-level
// severity/urgency isn't tied to one topic the way a single check-in's
// follow-up is). All four are written in ONE call at the end,
// teacher_log_reflection_cycle -- there is no separate "review" step,
// Act's own Save button is the submit. Reflect/Learn is read, not written:
// teacher_class_trend's response already carries the previous cycle back
// (last_cycle), shown alongside the current trend on the Sense step.

type ExpectedTrend = 'improving' | 'steady' | 'declining';
type InterpretationKind = 'workload' | 'social' | 'external_event' | 'seasonal' | 'unclear' | 'other';
const TRENDS: ExpectedTrend[] = ['improving', 'steady', 'declining'];
const KINDS: InterpretationKind[] = ['workload', 'social', 'external_event', 'seasonal', 'unclear', 'other'];

type ClassTrendSnapshot = { window_start: string; window_weeks: number; session_count: number; breakdown: Breakdown; mean_mood: number | null };
type ReflectionCycle = {
  id: string;
  expected_trend: ExpectedTrend;
  interpretation_kind: InterpretationKind;
  interpretation_note: string | null;
  playbook_key: string;
  trend_snapshot: ClassTrendSnapshot;
  created_at: string;
};
type ClassTrend = ClassTrendSnapshot & { last_cycle: ReflectionCycle | null };
type PbItem = { key: string; topic: string; title: Loc; body: Loc; is_draft: boolean; school_version: boolean };

type Step = 'before' | 'sense' | 'think' | 'act';
const STEPS: Step[] = ['before', 'sense', 'think', 'act'];

export function ReflectionPage() {
  const { t } = useI18n();
  const { classId = '' } = useParams<{ classId: string }>();
  const { data } = useTeacherHome();
  const cls = data?.classes.find((c) => c.id === classId);
  return (
    <TeacherFrame title={cls ? t['r6.tr.title'](cls.label) : t['teacher.title']} active="classes">
      {() => <Body classId={classId} className={cls?.label ?? ''} />}
    </TeacherFrame>
  );
}

function Body({ classId, className }: { classId: string; className: string }) {
  const { t, lang } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);

  const [step, setStep] = useState<Step>('before');
  const idx = STEPS.indexOf(step);
  const [expectedTrend, setExpectedTrend] = useState<ExpectedTrend | null>(null);
  const [trend, setTrend] = useState<ClassTrend | null>(null);
  const [trendErr, setTrendErr] = useState<AppError | null>(null);
  const [kind, setKind] = useState<InterpretationKind | null>(null);
  const [note, setNote] = useState('');
  const [items, setItems] = useState<PbItem[] | null>(null);
  const [playbookKey, setPlaybookKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<AppError | null>(null);
  const [done, setDone] = useState(false);

  useFocusHeadingOnNavigate(stepHeading);
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    stepHeading.current?.focus();
  }, [step]);

  const loadTrend = useCallback(async () => {
    const r = await rpc<ClassTrend>('teacher_class_trend', { p_class_id: classId });
    if (r.error) setTrendErr(r.error);
    else setTrend(r.data);
  }, [classId]);
  useEffect(() => {
    // The playbook list carries no anchoring risk the way the trend does
    // (Before must be answered before Sense reveals any data) -- fine to
    // fetch it up front.
    rpc<{ custom_allowed: boolean; items: PbItem[] }>('teacher_playbook').then((r) => {
      if (!r.error) setItems(r.data.items);
    });
  }, []);

  function next() {
    if (step === 'before') {
      if (!trend && !trendErr) loadTrend();
      setStep('sense');
    } else if (step === 'sense') {
      setStep('think');
    } else if (step === 'think') {
      setStep('act');
    }
  }
  function back() {
    if (idx > 0) setStep(STEPS[idx - 1]);
  }

  async function save() {
    if (!expectedTrend || !kind || !playbookKey) return;
    setBusy(true);
    setErr(null);
    const r = await rpc<{ id: string }>('teacher_log_reflection_cycle', {
      p_class_id: classId,
      p_expected_trend: expectedTrend,
      p_interpretation_kind: kind,
      p_playbook_key: playbookKey,
      p_interpretation_note: note.trim() || null,
    });
    setBusy(false);
    if (r.error) return setErr(r.error);
    announce(t['r6.tr.saved']);
    setDone(true);
  }

  const canNext = (step !== 'before' || expectedTrend !== null) && (step !== 'think' || kind !== null);
  const canSave = expectedTrend !== null && kind !== null && playbookKey !== '';
  const stepTitle: Record<Step, string> = {
    before: t['r6.tr.beforeTitle'],
    sense: t['r6.tr.senseTitle'],
    think: t['r6.tr.thinkTitle'],
    act: t['r6.tr.actTitle'],
  };

  if (done) {
    return (
      <section className="panel p5">
        <h2 className="panel-title">{t['r6.tr.doneTitle']}</h2>
        <p>{t['r6.tr.doneBody']}</p>
        <p><Link className="btn btn-ghost" to={`/teacher/class/${classId}`}>{t['r6.tr.backToClass']}</Link></p>
      </section>
    );
  }

  return (
    <>
      <p className="crumbs"><Link to={`/teacher/class/${classId}`}>← {className || t['teacher.title']}</Link></p>
      <section className="panel p5" aria-labelledby={`${id}-step`}>
        <ol className="stepper" aria-label={t['pa.wizSteps']}>
          {STEPS.map((s, i) => (
            <li key={s} className={i === idx ? 'on' : i < idx ? 'done' : ''} aria-current={i === idx ? 'step' : undefined}>
              <span className="stepper-n" aria-hidden="true">{i < idx ? '✓' : i + 1}</span>
              <span>{stepTitle[s]}</span>
              {i < idx && <span className="visually-hidden"> ({t['pa.done']})</span>}
            </li>
          ))}
        </ol>
        <h2 className="panel-title" id={`${id}-step`} ref={stepHeading} tabIndex={-1}>
          {t['pa.wizStepOf'](idx + 1, STEPS.length, stepTitle[step])}
        </h2>

        {step === 'before' && (
          <>
            <p className="form-hint">{t['r6.tr.beforeHint']}</p>
            <div className="chip-row" role="radiogroup" aria-labelledby={`${id}-step`}>
              {TRENDS.map((v) => (
                <label key={v} className={`chip-check${expectedTrend === v ? ' on' : ''}`}>
                  <input type="radio" name="trend" checked={expectedTrend === v} onChange={() => setExpectedTrend(v)} />
                  {t[`r6.tr.trend.${v}` as 'r6.tr.trend.improving']}
                </label>
              ))}
            </div>
          </>
        )}

        {step === 'sense' && (
          <>
            {trendErr && <p className="error-text" role="alert">{errorText(t, trendErr)}</p>}
            {!trend && !trendErr && <p role="status">{t.loading}</p>}
            {trend && (
              <>
                <p className="form-hint">{t['r6.tr.senseWindow'](trend.window_weeks)}</p>
                {trend.breakdown.status === 'insufficient' && (
                  <Suppressed title={t['sup.insufficientTitle']}>{t['ts.belowFloor'](trend.breakdown.total, trend.breakdown.floor)}</Suppressed>
                )}
                {trend.breakdown.status === 'suppressed_near_unanimous' && (
                  <Suppressed title={t['sup.unanimousTitle']}>{t['sup.unanimousClass'](trend.breakdown.total)}</Suppressed>
                )}
                {trend.breakdown.status === 'ok' && (
                  <>
                    <div className="score-line" role="group" aria-label={t['results.average']}>
                      <ScoreValue score={trend.breakdown.mean} className="score-num" />
                      <Pill kind={bandOf(trend.breakdown.mean).key}>{t[`band.${bandOf(trend.breakdown.mean).key}`]}</Pill>
                      <span className="text-muted" style={{ marginLeft: 'auto' }}>{t['results.responses'](trend.breakdown.total)}</span>
                    </div>
                    <MoodSplit labelledBy={`${id}-step`} result={trend.breakdown} />
                  </>
                )}
                {expectedTrend && <p className="form-hint">{t['r6.tr.youExpected'](t[`r6.tr.trend.${expectedTrend}` as 'r6.tr.trend.improving'])}</p>}
                {trend.last_cycle && (
                  <div className="notice notice-block">
                    <p><strong>{t['r6.tr.lastCycleTitle']}</strong></p>
                    <p>
                      {t['r6.tr.lastCycleBody'](
                        t[`r6.tr.trend.${trend.last_cycle.expected_trend}` as 'r6.tr.trend.improving'],
                        t[`r6.tr.kind.${trend.last_cycle.interpretation_kind}` as 'r6.tr.kind.workload'],
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {step === 'think' && (
          <>
            <p className="form-hint">{t['r6.tr.thinkHint']}</p>
            <div className="chip-row" role="radiogroup" aria-labelledby={`${id}-step`}>
              {KINDS.map((v) => (
                <label key={v} className={`chip-check${kind === v ? ' on' : ''}`}>
                  <input type="radio" name="kind" checked={kind === v} onChange={() => setKind(v)} />
                  {t[`r6.tr.kind.${v}` as 'r6.tr.kind.workload']}
                </label>
              ))}
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-note`}>{t['r6.tr.noteLabel']}</label>
              <textarea id={`${id}-note`} className="field" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </>
        )}

        {step === 'act' && (
          <>
            <p className="form-hint">{t['r6.tr.actHint']}</p>
            {!items && <p role="status">{t.loading}</p>}
            {items && (
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-pb`}>{t['r6.tr.playbookLabel']}</label>
                <div className="select-wrap">
                  <select id={`${id}-pb`} className="select" value={playbookKey} onChange={(e) => setPlaybookKey(e.target.value)}>
                    <option value="">{t['r6.tr.playbookPick']}</option>
                    {items.map((i) => (
                      <option key={i.key} value={i.key}>{loc(i.title, lang)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}

        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions">
          {idx > 0 && (
            <button type="button" className="btn btn-ghost" onClick={back} disabled={busy}>
              {t['pa.back']}
            </button>
          )}
          {step !== 'act' ? (
            <button type="button" className="btn btn-primary" disabled={!canNext} onClick={next}>
              {t['pa.next']}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={!canSave || busy} onClick={save}>
              {busy ? t['pa.working'] : t['r6.tr.save']}
            </button>
          )}
        </div>
      </section>
    </>
  );
}
